// =============================================================================
// REALISTIC DENTAL DEMO DATA - CUSTOMER READY
// All responses are dental-specific, professional, and realistic
// =============================================================================

export const demoMetrics = {
  realTime: {
    claimsProcessing: 5,
    activeVerifications: 4,
    denialsAnalyzing: 2,
    todayRevenue: 48015,
    processingTrend: '+2',
    verifyingTrend: 'Real-time',
    analyzingTrend: 'Active',
    revenueTrend: '+$8,200'
  },
  monthly: {
    totalClaims: 847,
    approvalRate: 91.5,
    denialRate: 8.5,
    averageReimbursement: 385,
    totalRevenue: 327495,
    timeToPayment: 23,
    cleanClaimRate: 94.2
  },
  arAging: {
    current: 45200,
    days30: 18300,
    days60: 6800,
    days90: 2700,
    total: 73000
  },
  denialTrends: [
    { month: 'Oct', rate: 18.4 },
    { month: 'Nov', rate: 15.1 },
    { month: 'Dec', rate: 11.6 },
    { month: 'Jan', rate: 8.3 },
    { month: 'Feb', rate: 5.7 },
    { month: 'Mar', rate: 3.2 }
  ],
  payerMix: [
    { name: 'Delta Dental', percentage: 32, amount: 104798 },
    { name: 'MetLife', percentage: 24, amount: 78599 },
    { name: 'Cigna', percentage: 18, amount: 58949 },
    { name: 'Aetna', percentage: 15, amount: 49124 },
    { name: 'Guardian', percentage: 11, amount: 36025 }
  ],
  production: {
    thisMonth: 127500,
    collections: 115200,
    gap: 12300,
    gapPercentage: 9.6
  }
};

export const demoPayerPerformance = [
  { payer: 'Delta Dental', claims: 234, denialRate: '6.2%', avgDays: 18, amount: '$187,450', trend: '↓' },
  { payer: 'MetLife', claims: 189, denialRate: '7.8%', avgDays: 21, amount: '$156,230', trend: '↓' },
  { payer: 'Cigna Dental', claims: 156, denialRate: '8.9%', avgDays: 24, amount: '$134,890', trend: '→' },
  { payer: 'Aetna', claims: 143, denialRate: '9.2%', avgDays: 26, amount: '$118,760', trend: '↑' },
  { payer: 'Guardian', claims: 125, denialRate: '10.5%', avgDays: 28, amount: '$98,340', trend: '→' }
];

export const demoInsuranceResponse = {
  success: true,
  status: 'success',
  processingTime: 1.8,
  patientEligible: true,
  coverage: {
    primary: {
      carrier: 'Delta Dental PPO',
      plan: 'PPO Plus',
      memberId: 'DD123456789',
      groupNumber: 'GRP-8472',
      effectiveDate: '2024-01-01',
      status: 'Active',
      subscriberName: 'John Smith',
      relationship: 'Self'
    },
    annualMaximum: {
      total: 2000,
      used: 550,
      remaining: 1450,
      percentageUsed: 27.5
    },
    deductible: {
      individual: {
        total: 50,
        met: 0,
        remaining: 50
      },
      family: {
        total: 150,
        met: 25,
        remaining: 125
      }
    },
    coverageLevels: [
      { 
        category: 'Preventive',
        coverage: '100%',
        procedures: 'Cleanings, Exams, X-rays',
        notes: 'No deductible, 2x per year'
      },
      { 
        category: 'Basic Restorative',
        coverage: '80%',
        procedures: 'Fillings, Extractions, Root Canals',
        notes: 'After deductible'
      },
      { 
        category: 'Major Restorative',
        coverage: '50%',
        procedures: 'Crowns, Bridges, Dentures',
        notes: 'After deductible, may require pre-auth'
      },
      { 
        category: 'Orthodontics',
        coverage: '50%',
        procedures: 'Braces, Aligners',
        notes: '$1,500 lifetime maximum'
      }
    ]
  },
  verificationDate: '2024-12-29',
  confidence: 98
};

export const demoDenialAnalysis = {
  success: true,
  status: 'analyzed',
  processingTime: 2.4,
  claimId: 'CLM-2026-0318',
  denialCode: 'CO-4',
  denialReason: 'Procedure not covered as submitted — incorrect tooth surface coding',
  procedureCode: 'D2750',
  procedureDescription: 'Crown - porcelain fused to metal',
  originalAmount: 890.00,
  deniedAmount: 890.00,
  patientResponsibility: null,
  analysis: {
    category: 'Coding Error',
    severity: 'Medium',
    recoverability: 91,
    timeToAppeal: '30 days',
    appealDeadline: '2026-05-02',
    appealsWon: '88% for this denial type'
  },
  aiRecommendations: [
    {
      priority: 'Critical',
      action: 'Resubmit with corrected tooth surface documentation',
      rationale: 'CO-4 denials are typically resolved by clarifying the submitted code with chart notes',
      successRate: 91,
      timeEstimate: '10 minutes'
    },
    {
      priority: 'High',
      action: 'Attach pre-treatment radiographs showing crown necessity',
      rationale: 'Visual documentation of tooth structure loss supports medical necessity',
      successRate: 82,
      timeEstimate: '5 minutes'
    },
    {
      priority: 'Medium',
      action: 'Include clinical notes documenting extent of decay',
      rationale: 'Establishes that a lesser restoration was not clinically appropriate',
      successRate: 74,
      timeEstimate: '10 minutes'
    }
  ],
  suggestedAppealLanguage: `Bright Smile Dental Group
1204 Oak Street, Suite 100
Austin, TX 78701
Phone: (512) 555-0192
NPI: 1578234690
Tax ID: 74-2819034

April 2, 2026

MetLife Dental
Appeals Department
PO Box 981282
El Paso, TX 79998

RE: Formal Appeal of Denied Claim
Patient: Sarah Mitchell
Date of Service: March 18, 2026
Procedure Code: D2750 — Porcelain-Fused-to-Metal Crown
Claim #: CLM-2026-0318
Claim Amount: $890.00
Denial Code: CO-4 (Procedure not covered as submitted)

Dear MetLife Appeals Department,

We are writing to formally appeal the denial of the above-referenced claim for patient Sarah Mitchell. The claim was denied under CO-4, indicating the procedure was not covered as submitted. We respectfully request that MetLife reconsider this determination based on the clinical documentation enclosed.

Clinical Justification:
The porcelain-fused-to-metal crown (D2750) placed on tooth #19 was the only clinically appropriate restorative option for this patient. Pre-treatment radiographs confirm extensive carious destruction involving the pulpal floor, with remaining tooth structure insufficient to support an onlay or large amalgam restoration. A crown was necessary to restore occlusal function and prevent fracture of the remaining tooth structure.

Correction of Submitted Information:
Upon review of the denial, we identified that the original submission required additional surface documentation. The following documentation checklist has been prepared to support this appeal. We believe this documentation will fully satisfy MetLife's coverage requirements for D2750.

Supporting Documentation Checklist (attach before submitting):
1. Updated ADA Dental Claim Form with corrected surface notation
2. Pre-treatment periapical and bitewing radiographs (dated March 14, 2026)
3. Clinical examination notes documenting extent of decay
4. Operative report and treatment consent form

We respectfully request full reconsideration and approval of this claim. Please contact our billing office at (512) 555-0192 or billing@brightsmiledental.com if additional information is required.

Sincerely,

Dr. Jennifer Caldwell, DDS
Bright Smile Dental Group`,
  similarCases: [
    { claimId: 'CLM-2024-12234', outcome: 'Approved on appeal', recoveryAmount: 1080, daysToResolution: 18 },
    { claimId: 'CLM-2024-13456', outcome: 'Approved on appeal', recoveryAmount: 1150, daysToResolution: 22 },
    { claimId: 'CLM-2024-14123', outcome: 'Partially approved', recoveryAmount: 805, daysToResolution: 28 }
  ],
  estimatedRecovery: 1001.25,
  confidence: 91
};

export const demoMedicalCoding = {
  success: true,
  status: 'coded',
  processingTime: 1.9,
  patientId: 'PT-10891',
  clinicalNotes: 'Root canal therapy tooth #30. Patient presents with acute pulpitis, spontaneous pain, positive percussion test. Pulp extirpation completed, canals shaped and irrigated. Obturation with gutta-percha. Post-op radiograph confirms complete fill.',
  suggestedCodes: {
    primary: [
      {
        code: 'D3330',
        description: 'Endodontic therapy, molar tooth (excluding final restoration)',
        confidence: 98,
        expectedReimbursement: 1020,
        documentation: 'Fully supported - molar RCT explicitly documented with obturation confirmed',
        denialRisk: 'Low (6%)',
        notes: 'Ensure post-op radiograph is attached — most payers require it'
      }
    ],
    additional: [
      {
        code: 'D9930',
        description: 'Treatment of complications (post-surgical) - unusual circumstances',
        confidence: 42,
        expectedReimbursement: 115,
        documentation: 'Only if significant complications documented',
        denialRisk: 'High (38%)',
        notes: 'Do not bill unless complications clearly noted in record'
      },
      {
        code: 'D2950',
        description: 'Core buildup, including pins',
        confidence: 81,
        expectedReimbursement: 185,
        documentation: 'Bill separately if buildup was performed prior to crown',
        denialRisk: 'Medium (14%)',
        notes: 'Common add-on after RCT — verify it was performed'
      }
    ],
    diagnosticCodes: [
      {
        code: 'K04.01',
        description: 'Reversible pulpitis',
        relevance: 'Primary diagnosis'
      },
      {
        code: 'K04.0',
        description: 'Pulpitis — irreversible, acute',
        relevance: 'Use if pulp was necrotic or irreversibly inflamed'
      }
    ]
  },
  complianceCheck: {
    passed: true,
    warnings: [],
    suggestions: [
      'Attach pre- and post-op radiographs showing canal obturation',
      'Document tooth number (#30) on all submitted forms',
      'Note number of canals treated — payers may verify for molar RCT'
    ]
  },
  recommendedModifiers: [],
  estimatedTotalReimbursement: 1020,
  denialPrediction: {
    likelihood: 'Low',
    percentage: 6,
    primaryRisk: 'Missing post-op radiograph'
  },
  confidence: 96
};

export const demoPreAuth = {
  success: true,
  status: 'generated',
  processingTime: 2.6,
  authNumber: 'PA-2024-821033',
  procedureRequested: {
    code: 'D8080',
    description: 'Comprehensive orthodontic treatment, adolescent dentition',
    toothNumber: 'Full arch',
    surfaces: 'Full orthodontic treatment',
    requestedDate: '2024-12-29',
    proposedTreatmentDate: '2025-01-22'
  },
  clinicalJustification: {
    diagnosis: 'M26.29 - Malocclusion, unspecified',
    symptoms: [
      'Significant crowding in both upper and lower arches',
      'Class II malocclusion with 5mm overjet',
      'Anterior crossbite affecting teeth #7 and #10',
      'Patient reports difficulty biting and chewing properly'
    ],
    imagingResults: 'Panoramic and cephalometric radiographs confirm skeletal Class II pattern with dental crowding. Space analysis shows 8mm arch length discrepancy.',
    priorTreatments: [
      'Phase I ortho evaluated — deferred to comprehensive treatment',
      'Patient monitored for 18 months — crowding has not self-corrected',
      'All permanent teeth fully erupted'
    ],
    alternativesConsidered: 'Limited orthodontic treatment considered but insufficient to correct skeletal discrepancy. Extraction of premolars may be required — to be determined mid-treatment.'
  },
  estimatedCosts: {
    procedureFee: 5800,
    insuranceCoverage: 1500,
    patientResponsibility: 4300,
    coveragePercentage: 26
  },
  generatedLetter: `TO: Prior Authorization Department
Cigna Dental PPO
FROM: [Practice Name]
RE: Prior Authorization Request - Comprehensive Orthodontic Treatment

Patient: Tyler Johnson
DOB: 03/12/2010
Member ID: CG887654321
Group Number: GRP-5521

REQUESTED PROCEDURE:
CDT Code D8080 - Comprehensive orthodontic treatment, adolescent dentition
Estimated Treatment Duration: 22-26 months
Proposed Start Date: January 22, 2025

DIAGNOSIS:
M26.29 - Malocclusion, unspecified
M26.212 - Malocclusion, Angle Class II

CLINICAL RATIONALE:
This 14-year-old patient presents with a significant Class II malocclusion requiring comprehensive orthodontic intervention. Clinical and radiographic findings include:

• 8mm arch length discrepancy with moderate-to-severe crowding in both arches
• 5mm overjet exceeding normal range
• Anterior crossbite on teeth #7 and #10 causing functional shift
• Cephalometric analysis confirms underlying skeletal pattern
• All permanent teeth erupted — optimal timing for treatment

PREVIOUS EVALUATION:
Phase I interceptive treatment was evaluated at age 10 and deferred. Patient has been monitored for 18 months with no spontaneous improvement. Comprehensive treatment is now indicated.

SUPPORTING DOCUMENTATION ATTACHED:
1. Panoramic radiograph
2. Cephalometric radiograph and tracing
3. Intraoral and extraoral photographs
4. Study models / digital scans
5. Treatment plan summary

Comprehensive orthodontic treatment is necessary to correct functional impairment, prevent long-term TMJ and periodontal complications, and restore proper occlusion.

Estimated Total Fee: $5,800.00
Insurance Benefit: $1,500.00 (lifetime orthodontic max)
Patient Responsibility: $4,300.00

Thank you for your timely review.

Sincerely,
Dr. Michael Thompson, DDS
NPI: 1234567890
Thompson Family Dentistry | (616) 555-0182`,
  estimatedApprovalTime: '5-7 business days',
  approvalProbability: 88,
  recommendedActions: [
    'Include cephalometric radiograph and tracing',
    'Attach intraoral photos showing crowding and crossbite',
    'Confirm lifetime ortho maximum has not been previously used',
    'Follow up in 5 business days if no response'
  ],
  confidence: 88
};

export const demoOCRResult = {
  success: true,
  status: 'extracted',
  processingTime: 1.9,
  cardType: 'dental-insurance',
  imageQuality: 'Excellent',
  extractedData: {
    memberInfo: {
      name: 'WILLIAMS, SARAH',
      memberId: 'GRD678901234',
      groupNumber: 'GRP-3344',
      dateOfBirth: '09/14/1995',
      gender: 'F'
    },
    carrierInfo: {
      name: 'GUARDIAN',
      planType: 'DentalGuard Preferred',
      effectiveDate: '01/01/2024',
      planYear: '2024'
    },
    benefits: {
      annualMaximum: '$1,500',
      deductible: '$75',
      preventiveCoverage: '100%',
      basicCoverage: '80%',
      majorCoverage: '50%'
    },
    contactNumbers: {
      memberServices: '1-888-600-1600',
      providerServices: '1-888-600-1601',
      claims: '1-888-600-1602'
    },
    eligibilityInfo: {
      status: 'Active',
      verifiedDate: '2024-12-29',
      waitingPeriods: 'None'
    }
  },
  dataQualityMetrics: {
    overallAccuracy: 97,
    memberIdConfidence: 99,
    groupNumberConfidence: 98,
    carrierConfidence: 96,
    benefitsConfidence: 94
  },
  validationResults: {
    readyToSubmit: true,
    warnings: [],
    recommendations: [
      'Card expiration date not visible - verify with carrier',
      'Consider secondary insurance verification'
    ]
  },
  autoPopulatedFields: [
    'Patient Name',
    'Date of Birth',
    'Member ID',
    'Group Number',
    'Insurance Carrier',
    'Plan Type'
  ],
  confidence: 97
};

export const demoClaimValidation = {
  success: true,
  status: 'validated',
  processingTime: 1.5,
  claimId: 'CLM-2024-NEW',
  validationResults: {
    overall: 'PASS',
    readyToSubmit: true,
    cleanClaimScore: 94
  },
  checks: {
    patient: {
      status: 'PASS',
      issues: [],
      details: 'All patient demographics verified'
    },
    insurance: {
      status: 'PASS',
      issues: [],
      details: 'Coverage active, benefits verified'
    },
    procedure: {
      status: 'PASS',
      issues: [],
      details: 'CDT codes valid and appropriate'
    },
    documentation: {
      status: 'WARNING',
      issues: ['Radiographs recommended but not required'],
      details: 'Clinical notes complete, consider adding X-rays'
    }
  },
  errors: [],
  warnings: [
    'Consider submitting radiographs to reduce chance of additional documentation request'
  ],
  suggestions: [
    'Claim meets all ADA Dental Claim Form requirements',
    'All required fields completed correctly',
    'Estimated clean claim rate: 94%',
    'Expected reimbursement within contract guidelines'
  ],
  estimatedReimbursement: {
    billed: 1350.00,
    expected: 1080.00,
    patientResponsibility: 270.00,
    paymentExpected: '18-23 days'
  },
  denialRiskAnalysis: {
    overallRisk: 'Low',
    percentage: 6,
    topRisks: [
      { factor: 'Missing documentation', risk: 3 },
      { factor: 'Coding accuracy', risk: 2 },
      { factor: 'Coverage limits', risk: 1 }
    ]
  },
  confidence: 94
};

export const demoPatientFinancials = {
  success: true,
  status: 'analyzed',
  processingTime: 1.7,
  data: {
  patientId: 'PT-10234',
  patientName: 'Sarah Mitchell',
  financialProfile: {
    currentBalance: '$112.00',
    insuranceBalance: '$0.00',
    patientBalance: '$112.00',
    collectionLikelihood: '87%',
    riskScore: 'Low',
    paymentHistory: 'Good - 94% on-time rate',
    accountAge: '1 month',
    previousPayments: 2,
    averagePaymentTime: '12 days'
  },
  recommendedStrategy: {
    approach: 'Payment Link — Already Sent',
    priority: 'Medium',
    reasoning: 'FlowRCM automatically sent Sarah an payment link by email the moment the MetLife ERA posted — 11 days ago. She has not yet paid. Her payment history is strong (94% on-time) and the balance is small ($112 patient responsibility after MetLife paid $778 on the D2750 crown). The recommended next step is a single follow-up reminder with the same payment link. High probability of payment within 7 days of follow-up.'
  },
  paymentOptions: [
    {
      plan: 'Pay in Full',
      terms: 'One-time payment',
      amount: '$112.00',
      recommended: true,
      reason: 'Small balance — fastest resolution with no follow-up needed'
    },
    {
      plan: '2-Month Plan',
      terms: '2 monthly payments',
      amount: '$56.00/month',
      recommended: false,
      reason: 'Option for patients preferring to split the balance'
    },
    {
      plan: '3-Month Plan',
      terms: '3 monthly payments',
      amount: '$37.33/month',
      recommended: false,
      reason: 'Lowest monthly payment option'
    }
  ],
  sampleMessage: `Dear Sarah Mitchell,

A payment of $778 from MetLife has been applied to your account for the porcelain crown (D2750) on March 18th. Your remaining patient responsibility is $112.00.

You can pay securely online — card, bank transfer, HSA, or FSA accepted:

👉 [Pay $112.00 online — takes 2 minutes]

Questions? Call us at (555) 123-4567.

Thank you,
[Practice Name]

—
This payment link was sent automatically by FlowRCM the moment your insurance posted.`,
  communicationPlan: {
    preferredChannel: 'payment link via email (Stripe)',
    suggestedTiming: 'Weekday evenings 5-7 PM',
    tone: 'Friendly and professional',
    followUpSchedule: ['Initial contact today', 'Follow-up in 7 days', 'Second follow-up in 14 days', 'Phone call at 21 days']
  },
  escalationPlan: {
    trigger: 'No response after 30 days or 3 contact attempts',
    actions: ['Send certified letter', 'Offer hardship review', 'Consider payment plan adjustment', 'Final notice before collections']
  },
  confidence: 89
  } // end data
};

// ==================== DEMO CLAIMS DATA ====================
export const demoClaims = [
  { id: 'demo-claim-001', claim_number: 'CLM-2026-0318', patient_name: 'Sarah Mitchell', payer_name: 'MetLife Dental', claim_status: 'Denied', claim_amount: 890, claim_paid_amount: 0, service_date: '2026-03-18', procedure_codes: ['D2750'], denial_code: 'CO-4', denial_reason: 'Procedure not covered as submitted', notes: 'Denied CO-4: procedure not covered as submitted', created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-002', claim_number: 'CLM-2026-0315', patient_name: 'James Torres', payer_name: 'Delta Dental PPO', claim_status: 'Denied', claim_amount: 1240, claim_paid_amount: 0, service_date: '2026-03-15', procedure_codes: ['D3330'], notes: 'Denied CO-29: services not covered for frequency', created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-003', claim_number: 'CLM-2026-0322', patient_name: 'Maria Garcia', payer_name: 'Cigna Dental', claim_status: 'Submitted', claim_amount: 340, claim_paid_amount: 0, service_date: '2026-03-22', procedure_codes: ['D1110'], created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-004', claim_number: 'CLM-2026-0310', patient_name: 'Robert Chen', payer_name: 'Aetna Dental', claim_status: 'Paid', claim_amount: 1820, claim_paid_amount: 1456, service_date: '2026-03-10', procedure_codes: ['D6010', 'D6065'], created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-005', claim_number: 'CLM-2026-0320', patient_name: 'Linda Thompson', payer_name: 'Blue Cross Blue Shield', claim_status: 'Submitted', claim_amount: 960, claim_paid_amount: 0, service_date: '2026-03-20', procedure_codes: ['D4341', 'D4342'], created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-006', claim_number: 'CLM-2026-0308', patient_name: 'Kevin Park', payer_name: 'Delta Dental PPO', claim_status: 'Paid', claim_amount: 780, claim_paid_amount: 624, service_date: '2026-03-08', procedure_codes: ['D2950', 'D2740'], created_at: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-007', claim_number: 'CLM-2026-0324', patient_name: 'Emily Davis', payer_name: 'Guardian Dental', claim_status: 'AI Flagged', claim_amount: 1550, claim_paid_amount: 0, service_date: '2026-03-24', procedure_codes: ['D7210'], scrub_risk_score: 72, scrub_risk_level: 'High', scrub_issues: [{ title: 'Missing pre-op radiograph', fix: 'Attach periapical X-ray taken within 6 months of service date — Guardian requires this for D7210', severity: 'High' }, { title: 'Clinical notes not attached', fix: 'Include documentation of tooth condition and medical necessity for surgical extraction', severity: 'Medium' }], automation_status: 'completed', created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-008', claim_number: 'CLM-2026-0321', patient_name: 'Tyler Johnson', payer_name: 'Cigna Dental', claim_status: 'Submitted', claim_amount: 2200, claim_paid_amount: 0, service_date: '2026-03-21', procedure_codes: ['D8080'], created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-009', claim_number: 'CLM-2026-0305', patient_name: 'Patricia Wong', payer_name: 'MetLife Dental', claim_status: 'Paid', claim_amount: 420, claim_paid_amount: 336, service_date: '2026-03-05', procedure_codes: ['D0330', 'D0210'], created_at: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-010', claim_number: 'CLM-2026-0312', patient_name: 'David Martinez', payer_name: 'Aetna Dental', claim_status: 'Denied', claim_amount: 560, claim_paid_amount: 0, service_date: '2026-03-12', procedure_codes: ['D2391'], notes: 'Denied CO-97: payment included in allowance for another service', created_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-011', claim_number: 'CLM-2026-0228', patient_name: 'Angela Foster', payer_name: 'United Concordia', claim_status: 'Paid', claim_amount: 720, claim_paid_amount: 576, service_date: '2026-02-28', procedure_codes: ['D2751'], created_at: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-012', claim_number: 'CLM-2026-0225', patient_name: 'Marcus Lee', payer_name: 'Delta Dental PPO', claim_status: 'Paid', claim_amount: 1680, claim_paid_amount: 1344, service_date: '2026-02-25', procedure_codes: ['D6010', 'D6040'], created_at: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-013', claim_number: 'CLM-2026-0220', patient_name: 'Sandra Hill', payer_name: 'Cigna Dental', claim_status: 'Paid', claim_amount: 310, claim_paid_amount: 248, service_date: '2026-02-20', procedure_codes: ['D1110', 'D0274'], created_at: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-014', claim_number: 'CLM-2026-0218', patient_name: 'Brandon Scott', payer_name: 'MetLife Dental', claim_status: 'Denied', claim_amount: 890, claim_paid_amount: 0, service_date: '2026-02-18', procedure_codes: ['D4341'], denial_code: 'CO-29', denial_reason: 'Services not covered for frequency', notes: 'Denied CO-29: frequency limitation exceeded', created_at: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-015', claim_number: 'CLM-2026-0215', patient_name: 'Christine Adams', payer_name: 'Blue Cross Blue Shield', claim_status: 'Paid', claim_amount: 2400, claim_paid_amount: 1920, service_date: '2026-02-15', procedure_codes: ['D2740', 'D2950'], created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-016', claim_number: 'CLM-2026-0210', patient_name: 'Nathan Rivera', payer_name: 'Aetna Dental', claim_status: 'Paid', claim_amount: 460, claim_paid_amount: 368, service_date: '2026-02-10', procedure_codes: ['D0330'], created_at: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-017', claim_number: 'CLM-2026-0205', patient_name: 'Jessica Turner', payer_name: 'Guardian Dental', claim_status: 'Paid', claim_amount: 1100, claim_paid_amount: 880, service_date: '2026-02-05', procedure_codes: ['D3310', 'D3320'], created_at: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-018', claim_number: 'CLM-2026-0130', patient_name: 'Thomas Baker', payer_name: 'Delta Dental PPO', claim_status: 'Paid', claim_amount: 640, claim_paid_amount: 512, service_date: '2026-01-30', procedure_codes: ['D2391', 'D2392'], created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-019', claim_number: 'CLM-2026-0125', patient_name: 'Rachel Green', payer_name: 'United Concordia', claim_status: 'Paid', claim_amount: 870, claim_paid_amount: 696, service_date: '2026-01-25', procedure_codes: ['D7210'], created_at: new Date(Date.now() - 65 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-020', claim_number: 'CLM-2026-0120', patient_name: 'Anthony Nelson', payer_name: 'Cigna Dental', claim_status: 'Denied', claim_amount: 1350, claim_paid_amount: 0, service_date: '2026-01-20', procedure_codes: ['D6065'], denial_code: 'CO-4', denial_reason: 'Procedure not covered as submitted', notes: 'Denied CO-4: missing pre-auth documentation', created_at: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-021', claim_number: 'CLM-2026-0115', patient_name: 'Stephanie Collins', payer_name: 'MetLife Dental', claim_status: 'Paid', claim_amount: 390, claim_paid_amount: 312, service_date: '2026-01-15', procedure_codes: ['D1110', 'D0210'], created_at: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-022', claim_number: 'CLM-2026-0110', patient_name: 'Gregory White', payer_name: 'Blue Cross Blue Shield', claim_status: 'Paid', claim_amount: 1760, claim_paid_amount: 1408, service_date: '2026-01-10', procedure_codes: ['D3330', 'D3332'], created_at: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-023', claim_number: 'CLM-2026-0105', patient_name: 'Amanda Price', payer_name: 'Aetna Dental', claim_status: 'Paid', claim_amount: 520, claim_paid_amount: 416, service_date: '2026-01-05', procedure_codes: ['D2160'], created_at: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-024', claim_number: 'CLM-2025-1228', patient_name: 'Christopher Evans', payer_name: 'Guardian Dental', claim_status: 'Paid', claim_amount: 2950, claim_paid_amount: 2360, service_date: '2025-12-28', procedure_codes: ['D6010', 'D6065', 'D6066'], created_at: new Date(Date.now() - 92 * 24 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-claim-025', claim_number: 'CLM-2025-1220', patient_name: 'Michelle Howard', payer_name: 'Delta Dental PPO', claim_status: 'Paid', claim_amount: 680, claim_paid_amount: 544, service_date: '2025-12-20', procedure_codes: ['D4910'], created_at: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString() },
];

// Demo patients for patient search dropdown (matches seeded patients)
export const demoPatients = [
  { id: 'demo-p-01', open_dental_patient_id: 'PT-10001', first_name: 'John',        last_name: 'Smith',    date_of_birth: '1975-03-15', phone: '555-123-4567', email: 'john.smith@email.com',       insurance_carrier: 'Delta Dental',     insurance_member_id: 'DD123456789',  outstanding_balance: 125.00,  created_at: new Date(Date.now() - 120*24*3600*1000).toISOString() },
  { id: 'demo-p-02', open_dental_patient_id: 'PT-10002', first_name: 'Jane',        last_name: 'Doe',      date_of_birth: '1982-07-22', phone: '555-987-6543', email: 'jane.doe@email.com',         insurance_carrier: 'MetLife',          insurance_member_id: 'ML987654321',  outstanding_balance: 450.00,  created_at: new Date(Date.now() - 110*24*3600*1000).toISOString() },
  { id: 'demo-p-03', open_dental_patient_id: 'PT-10003', first_name: 'Robert',      last_name: 'Johnson',  date_of_birth: '1990-11-08', phone: '555-456-7890', email: 'robert.j@email.com',         insurance_carrier: 'Cigna',            insurance_member_id: 'CIG456789012', outstanding_balance: 0.00,    created_at: new Date(Date.now() - 100*24*3600*1000).toISOString() },
  { id: 'demo-p-04', open_dental_patient_id: 'PT-10004', first_name: 'Maria',       last_name: 'Garcia',   date_of_birth: '1988-05-20', phone: '555-234-5678', email: 'maria.garcia@email.com',     insurance_carrier: 'Delta Dental',     insurance_member_id: 'DD234567890',  outstanding_balance: 175.00,  created_at: new Date(Date.now() - 95*24*3600*1000).toISOString() },
  { id: 'demo-p-05', open_dental_patient_id: 'PT-10005', first_name: 'William',     last_name: 'Brown',    date_of_birth: '1965-12-01', phone: '555-345-6789', email: 'wbrown@email.com',           insurance_carrier: 'Aetna',            insurance_member_id: 'AET567890123', outstanding_balance: 0.00,    created_at: new Date(Date.now() - 90*24*3600*1000).toISOString() },
  { id: 'demo-p-06', open_dental_patient_id: 'PT-10006', first_name: 'Sarah',       last_name: 'Mitchell', date_of_birth: '1995-09-14', phone: '555-567-8901', email: 'sarah.m@email.com',          insurance_carrier: 'MetLife',          insurance_member_id: 'ML567890123',  outstanding_balance: 112.00,  created_at: new Date(Date.now() - 85*24*3600*1000).toISOString() },
  { id: 'demo-p-07', open_dental_patient_id: 'PT-10007', first_name: 'Michael',     last_name: 'Davis',    date_of_birth: '1978-01-30', phone: '555-678-9012', email: 'mdavis@email.com',           insurance_carrier: 'United Concordia', insurance_member_id: 'UC789012345',  outstanding_balance: 320.00,  created_at: new Date(Date.now() - 80*24*3600*1000).toISOString() },
  { id: 'demo-p-08', open_dental_patient_id: 'PT-10008', first_name: 'Emily',       last_name: 'Martinez', date_of_birth: '2001-04-18', phone: '555-789-0123', email: 'emily.m@email.com',          insurance_carrier: 'Delta Dental',     insurance_member_id: 'DD345678901',  outstanding_balance: 0.00,    created_at: new Date(Date.now() - 75*24*3600*1000).toISOString() },
  { id: 'demo-p-09', open_dental_patient_id: 'PT-10009', first_name: 'David',       last_name: 'Anderson', date_of_birth: '1958-08-25', phone: '555-890-1234', email: 'danderson@email.com',        insurance_carrier: 'MetLife',          insurance_member_id: 'ML234567890',  outstanding_balance: 1250.00, created_at: new Date(Date.now() - 70*24*3600*1000).toISOString() },
  { id: 'demo-p-10', open_dental_patient_id: 'PT-10010', first_name: 'Lisa',        last_name: 'Thompson', date_of_birth: '1992-06-07', phone: '555-901-2345', email: 'lisa.t@email.com',           insurance_carrier: 'Cigna',            insurance_member_id: 'CIG567890123', outstanding_balance: 0.00,    created_at: new Date(Date.now() - 65*24*3600*1000).toISOString() },
  { id: 'demo-p-11', open_dental_patient_id: 'PT-10011', first_name: 'James',       last_name: 'Wilson',   date_of_birth: '1970-02-14', phone: '555-112-3344', email: 'jwilson@email.com',          insurance_carrier: 'Delta Dental',     insurance_member_id: 'DD456789012',  outstanding_balance: 0.00,    created_at: new Date(Date.now() - 60*24*3600*1000).toISOString() },
  { id: 'demo-p-12', open_dental_patient_id: 'PT-10012', first_name: 'Patricia',    last_name: 'Moore',    date_of_birth: '1985-10-03', phone: '555-223-4455', email: 'p.moore@email.com',          insurance_carrier: 'Aetna',            insurance_member_id: 'AET678901234', outstanding_balance: 295.00,  created_at: new Date(Date.now() - 55*24*3600*1000).toISOString() },
  { id: 'demo-p-13', open_dental_patient_id: 'PT-10013', first_name: 'Christopher', last_name: 'Taylor',   date_of_birth: '1998-06-28', phone: '555-334-5566', email: 'chris.t@email.com',          insurance_carrier: 'Guardian',         insurance_member_id: 'GRD789012345', outstanding_balance: 0.00,    created_at: new Date(Date.now() - 50*24*3600*1000).toISOString() },
  { id: 'demo-p-14', open_dental_patient_id: 'PT-10014', first_name: 'Jennifer',    last_name: 'White',    date_of_birth: '1973-11-19', phone: '555-445-6677', email: 'jwhite@email.com',           insurance_carrier: 'MetLife',          insurance_member_id: 'ML345678901',  outstanding_balance: 560.00,  created_at: new Date(Date.now() - 45*24*3600*1000).toISOString() },
  { id: 'demo-p-15', open_dental_patient_id: 'PT-10015', first_name: 'Daniel',      last_name: 'Harris',   date_of_birth: '1962-04-07', phone: '555-556-7788', email: 'dharris@email.com',          insurance_carrier: 'Cigna',            insurance_member_id: 'CIG678901234', outstanding_balance: 0.00,    created_at: new Date(Date.now() - 40*24*3600*1000).toISOString() },
];

export const demoClaimsByPatientId = {
  'demo-p-01': [
    { id: 'dc-01-1', claim_number: 'CLM-2026-0201', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2026-02-01', payer_name: 'Delta Dental',     patient_name: 'John Smith' },
    { id: 'dc-01-2', claim_number: 'CLM-2026-0089', claim_status: 'Pending',   claim_amount: 125,  claim_paid_amount: 0,    procedure_codes: ['D2150'], service_date: '2026-01-15', payer_name: 'Delta Dental',     patient_name: 'John Smith' },
  ],
  'demo-p-02': [
    { id: 'dc-02-1', claim_number: 'CLM-2026-0312', claim_status: 'Denied',    claim_amount: 890,  claim_paid_amount: 0,    procedure_codes: ['D2750'], service_date: '2026-03-05', payer_name: 'MetLife',          patient_name: 'Jane Doe', denial_reason: 'CO-4 — Procedure not covered as submitted' },
    { id: 'dc-02-2', claim_number: 'CLM-2026-0198', claim_status: 'Paid',      claim_amount: 450,  claim_paid_amount: 360,  procedure_codes: ['D3330'], service_date: '2026-02-14', payer_name: 'MetLife',          patient_name: 'Jane Doe' },
    { id: 'dc-02-3', claim_number: 'CLM-2025-1102', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2025-11-02', payer_name: 'MetLife',          patient_name: 'Jane Doe' },
  ],
  'demo-p-03': [
    { id: 'dc-03-1', claim_number: 'CLM-2026-0287', claim_status: 'Paid',      claim_amount: 1350, claim_paid_amount: 1080, procedure_codes: ['D3330'], service_date: '2026-02-28', payer_name: 'Cigna',            patient_name: 'Robert Johnson' },
    { id: 'dc-03-2', claim_number: 'CLM-2026-0144', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 220,  procedure_codes: ['D1110'], service_date: '2026-01-22', payer_name: 'Cigna',            patient_name: 'Robert Johnson' },
  ],
  'demo-p-04': [
    { id: 'dc-04-1', claim_number: 'CLM-2026-0334', claim_status: 'Submitted', claim_amount: 340,  claim_paid_amount: 0,    procedure_codes: ['D2391'], service_date: '2026-03-10', payer_name: 'Delta Dental',     patient_name: 'Maria Garcia' },
    { id: 'dc-04-2', claim_number: 'CLM-2026-0112', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2026-01-08', payer_name: 'Delta Dental',     patient_name: 'Maria Garcia' },
  ],
  'demo-p-05': [
    { id: 'dc-05-1', claim_number: 'CLM-2026-0255', claim_status: 'Paid',      claim_amount: 780,  claim_paid_amount: 624,  procedure_codes: ['D4341'], service_date: '2026-02-20', payer_name: 'Aetna',            patient_name: 'William Brown' },
    { id: 'dc-05-2', claim_number: 'CLM-2025-1215', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2025-12-15', payer_name: 'Aetna',            patient_name: 'William Brown' },
  ],
  'demo-p-06': [
    { id: 'demo-claim-sarah', claim_number: 'CLM-2026-0318', claim_status: 'Paid', claim_amount: 890, claim_paid_amount: 778, procedure_codes: ['D2750'], service_date: '2026-03-18', payer_name: 'MetLife Dental', patient_name: 'Sarah Mitchell' },
  ],
  'demo-p-07': [
    { id: 'dc-07-1', claim_number: 'CLM-2026-0401', claim_status: 'In Review', claim_amount: 1680, claim_paid_amount: 0,    procedure_codes: ['D6010'], service_date: '2026-03-22', payer_name: 'United Concordia', patient_name: 'Michael Davis' },
    { id: 'dc-07-2', claim_number: 'CLM-2026-0211', claim_status: 'Paid',      claim_amount: 320,  claim_paid_amount: 256,  procedure_codes: ['D2740'], service_date: '2026-02-11', payer_name: 'United Concordia', patient_name: 'Michael Davis' },
  ],
  'demo-p-08': [
    { id: 'dc-08-1', claim_number: 'CLM-2026-0298', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 220,  procedure_codes: ['D1110'], service_date: '2026-03-01', payer_name: 'Delta Dental',     patient_name: 'Emily Martinez' },
    { id: 'dc-08-2', claim_number: 'CLM-2025-1108', claim_status: 'Paid',      claim_amount: 185,  claim_paid_amount: 148,  procedure_codes: ['D2150'], service_date: '2025-11-08', payer_name: 'Delta Dental',     patient_name: 'Emily Martinez' },
  ],
  'demo-p-09': [
    { id: 'dc-09-1', claim_number: 'CLM-2026-0388', claim_status: 'Denied',    claim_amount: 1820, claim_paid_amount: 0,    procedure_codes: ['D8080'], service_date: '2026-03-15', payer_name: 'MetLife',          patient_name: 'David Anderson', denial_reason: 'CO-97 — Benefit for this service is included in another allowance' },
    { id: 'dc-09-2', claim_number: 'CLM-2026-0229', claim_status: 'Appealed',  claim_amount: 960,  claim_paid_amount: 0,    procedure_codes: ['D4341'], service_date: '2026-02-18', payer_name: 'MetLife',          patient_name: 'David Anderson' },
    { id: 'dc-09-3', claim_number: 'CLM-2026-0101', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2026-01-05', payer_name: 'MetLife',          patient_name: 'David Anderson' },
  ],
  'demo-p-10': [
    { id: 'dc-10-1', claim_number: 'CLM-2026-0362', claim_status: 'Paid',      claim_amount: 756,  claim_paid_amount: 605,  procedure_codes: ['D2740'], service_date: '2026-03-12', payer_name: 'Cigna',            patient_name: 'Lisa Thompson' },
    { id: 'dc-10-2', claim_number: 'CLM-2025-1220', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2025-12-20', payer_name: 'Cigna',            patient_name: 'Lisa Thompson' },
  ],
  'demo-p-11': [
    { id: 'dc-11-1', claim_number: 'CLM-2026-0276', claim_status: 'Paid',      claim_amount: 1080, claim_paid_amount: 864,  procedure_codes: ['D3330'], service_date: '2026-02-25', payer_name: 'Delta Dental',     patient_name: 'James Wilson' },
    { id: 'dc-11-2', claim_number: 'CLM-2026-0133', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 220,  procedure_codes: ['D1110'], service_date: '2026-01-18', payer_name: 'Delta Dental',     patient_name: 'James Wilson' },
  ],
  'demo-p-12': [
    { id: 'dc-12-1', claim_number: 'CLM-2026-0419', claim_status: 'Submitted', claim_amount: 945,  claim_paid_amount: 0,    procedure_codes: ['D2750'], service_date: '2026-03-25', payer_name: 'Aetna',            patient_name: 'Patricia Moore' },
    { id: 'dc-12-2', claim_number: 'CLM-2026-0188', claim_status: 'Paid',      claim_amount: 295,  claim_paid_amount: 236,  procedure_codes: ['D2391'], service_date: '2026-02-08', payer_name: 'Aetna',            patient_name: 'Patricia Moore' },
  ],
  'demo-p-13': [
    { id: 'dc-13-1', claim_number: 'CLM-2026-0244', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 220,  procedure_codes: ['D1110'], service_date: '2026-02-22', payer_name: 'Guardian',         patient_name: 'Christopher Taylor' },
    { id: 'dc-13-2', claim_number: 'CLM-2025-1115', claim_status: 'Paid',      claim_amount: 310,  claim_paid_amount: 248,  procedure_codes: ['D0330'], service_date: '2025-11-15', payer_name: 'Guardian',         patient_name: 'Christopher Taylor' },
  ],
  'demo-p-14': [
    { id: 'dc-14-1', claim_number: 'CLM-2026-0377', claim_status: 'Denied',    claim_amount: 1240, claim_paid_amount: 0,    procedure_codes: ['D6065'], service_date: '2026-03-14', payer_name: 'MetLife',          patient_name: 'Jennifer White', denial_reason: 'CO-4 — Missing pre-authorization' },
    { id: 'dc-14-2', claim_number: 'CLM-2026-0261', claim_status: 'Paid',      claim_amount: 560,  claim_paid_amount: 448,  procedure_codes: ['D4341'], service_date: '2026-02-24', payer_name: 'MetLife',          patient_name: 'Jennifer White' },
    { id: 'dc-14-3', claim_number: 'CLM-2025-1201', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2025-12-01', payer_name: 'MetLife',          patient_name: 'Jennifer White' },
  ],
  'demo-p-15': [
    { id: 'dc-15-1', claim_number: 'CLM-2026-0322', claim_status: 'Paid',      claim_amount: 1100, claim_paid_amount: 880,  procedure_codes: ['D7210'], service_date: '2026-03-08', payer_name: 'Cigna',            patient_name: 'Daniel Harris' },
    { id: 'dc-15-2', claim_number: 'CLM-2026-0155', claim_status: 'Paid',      claim_amount: 220,  claim_paid_amount: 176,  procedure_codes: ['D1110'], service_date: '2026-01-28', payer_name: 'Cigna',            patient_name: 'Daniel Harris' },
  ],
};

// ==================== AUTOMATION DEMO DATA ====================

export const demoAutomationActivity = {
  todaySummary: {
    auto_appeal_generated: 3,
    claim_aging_flagged: 2,
    claim_scrubbed: 7,
    payment_reminder_sent: 4,
    appeal_faxed: 2,
    era_auto_matched: 1,
    secondary_claim_created: 1,
    benefits_verified: 2,
  },
  activity: [
    { action: 'appeal_faxed',           metadata: { patient: 'Sarah Mitchell', payer: 'MetLife', amount: 890, fax_number: '(verified)' }, created_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString() },
    { action: 'appeal_faxed',           metadata: { patient: 'James Torres', payer: 'Delta Dental', amount: 1240, fax_number: '(verified)' }, created_at: new Date(Date.now() - 1.6 * 60 * 60 * 1000).toISOString() },
    { action: 'auto_appeal_generated',  metadata: { patient: 'Maria Garcia', payer: 'Cigna', amount: 340 }, created_at: new Date(Date.now() - 1.8 * 60 * 60 * 1000).toISOString() },
    { action: 'appeal_held',            metadata: { patient: 'Tom Wilson', payer: 'Guardian', reason: 'payer_not_verified' }, created_at: new Date(Date.now() - 1.9 * 60 * 60 * 1000).toISOString() },
    { action: 'era_auto_matched',       metadata: { patient: 'Jennifer White', payer: 'Cigna', amount: 756 }, created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
    { action: 'secondary_claim_created', metadata: { patient: 'Emily Davis', primary_payer: 'MetLife', secondary_payer: 'Cigna' }, created_at: new Date(Date.now() - 2.1 * 60 * 60 * 1000).toISOString() },
    { action: 'benefits_verified',      metadata: { patient: 'Robert Chen', payer: 'Aetna', remaining: 1500 }, created_at: new Date(Date.now() - 2.2 * 60 * 60 * 1000).toISOString() },
    { action: 'claim_aging_flagged',    metadata: { patient_name: 'Robert Chen', payer: 'Aetna', amount: 1820, days_old: 52, priority: 'HIGH' }, created_at: new Date(Date.now() - 2.3 * 60 * 60 * 1000).toISOString() },
    { action: 'claim_aging_flagged',    metadata: { patient_name: 'Linda Thompson', payer: 'Blue Cross', amount: 960, days_old: 47, priority: 'MEDIUM' }, created_at: new Date(Date.now() - 2.4 * 60 * 60 * 1000).toISOString() },
    { action: 'claim_scrubbed',         metadata: { patient: 'Kevin Park', payer: 'Delta Dental', amount: 780, risk_level: 'Low' }, created_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString() },
    { action: 'payment_reminder_sent',  metadata: { patient: 'Emily Davis', amount: 245 }, created_at: new Date(Date.now() - 2.6 * 60 * 60 * 1000).toISOString() },
    { action: 'claim_scrubbed',         metadata: { patient: 'Tom Wilson', payer: 'Cigna', amount: 1100, risk_level: 'Medium' }, created_at: new Date(Date.now() - 2.7 * 60 * 60 * 1000).toISOString() },
  ]
};

// ==================== DEMO PAYERS ====================
export const demoPayers = [
  { id: 'demo-payer-001', payer_name: 'Delta Dental', payer_id: '86027', appeal_method: 'fax', appeal_fax: '(555) 100-1001', appeal_email: null, appeal_portal_url: 'https://www.deltadental.com/providers', appeal_notes: 'Fax appeals. Include EOB and clinical notes.', human_verified: true, human_verified_by: 'admin', human_verified_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), requires_preauth: false },
  { id: 'demo-payer-002', payer_name: 'MetLife', payer_id: '65978', appeal_method: 'fax', appeal_fax: '(555) 100-1002', appeal_email: null, appeal_portal_url: null, appeal_notes: 'Fax appeals. Allow 30 days for review.', human_verified: true, human_verified_by: 'admin', human_verified_at: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(), requires_preauth: false },
  { id: 'demo-payer-003', payer_name: 'Cigna', payer_id: '62308', appeal_method: 'fax', appeal_fax: '(555) 100-1003', appeal_email: null, appeal_portal_url: 'https://cignaforhcp.cigna.com', appeal_notes: 'Appeals via fax or Cigna provider portal.', human_verified: true, human_verified_by: 'admin', human_verified_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(), requires_preauth: true },
  { id: 'demo-payer-004', payer_name: 'Aetna', payer_id: '60054', appeal_method: 'fax', appeal_fax: '(555) 100-1004', appeal_email: null, appeal_portal_url: null, appeal_notes: 'Fax or Availity portal. 30-day review window.', human_verified: false, human_verified_by: null, human_verified_at: null, requires_preauth: true },
  { id: 'demo-payer-005', payer_name: 'Guardian', payer_id: '66005', appeal_method: 'fax', appeal_fax: '(555) 100-1005', appeal_email: null, appeal_portal_url: null, appeal_notes: 'AI suggested — verify before enabling auto-send.', human_verified: false, human_verified_by: null, human_verified_at: null, requires_preauth: false },
  { id: 'demo-payer-006', payer_name: 'United Concordia', payer_id: '85729', appeal_method: 'fax', appeal_fax: '(555) 100-1006', appeal_email: null, appeal_portal_url: null, appeal_notes: 'Fax preferred. Include EOB copy.', human_verified: true, human_verified_by: 'admin', human_verified_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), requires_preauth: false },
];

// ==================== ERA DEMO DATA ====================
export const demoERAPayments = [
  {
    id: 'era-demo-001',
    payer_name: 'Delta Dental PPO',
    check_number: 'DD-20260318-4821',
    check_date: '2026-03-18',
    payment_date: '2026-03-18',
    payment_method: 'EFT',
    total_amount: 2847.00,
    applied_amount: 2847.00,
    status: 'Applied',
    notes: 'Auto-posted by FlowRCM — 4 claims reconciled',
    created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    adjustments: [
      { claim_id: null, patient_name: 'James Torres',    procedure_code: 'D3330', billed: 1350, paid: 1080, adjustment: 270, reason: 'CO-45 — Contractual adjustment' },
      { claim_id: null, patient_name: 'Maria Garcia',    procedure_code: 'D1110', billed: 220,  paid: 175,  adjustment: 45,  reason: 'CO-45 — Contractual adjustment' },
      { claim_id: null, patient_name: 'Kevin Park',      procedure_code: 'D0330', billed: 310,  paid: 248,  adjustment: 62,  reason: 'CO-45 — Contractual adjustment' },
      { claim_id: null, patient_name: 'Linda Thompson',  procedure_code: 'D4341', billed: 1680, paid: 1344, adjustment: 336, reason: 'CO-45 — Contractual adjustment' },
    ]
  },
  {
    id: 'era-demo-002',
    payer_name: 'MetLife Dental',
    check_number: 'ML-20260414-9934',
    check_date: '2026-04-14',
    payment_date: '2026-04-14',
    payment_method: 'Insurance Payment',
    total_amount: 778.00,
    applied_amount: 778.00,
    status: 'Applied',
    notes: 'Appeal approved — D2750 crown · Previously denied CO-4 · Auto-posted by FlowRCM · payment link auto-sent to patient for $112 balance',
    created_at: '2026-04-14T10:00:00.000Z',
    adjustments: [
      { claim_id: null, patient_name: 'Sarah Mitchell', procedure_code: 'D2750', billed: 890, paid: 778, adjustment: 0, reason: 'Appeal approved — MetLife contracted rate, $112 patient responsibility' },
    ]
  },
  {
    id: 'era-demo-003',
    payer_name: 'Cigna Dental',
    check_number: 'CG-20260322-7710',
    check_date: '2026-03-22',
    payment_date: '2026-03-22',
    payment_method: 'EFT',
    total_amount: 1340.00,
    applied_amount: 0,
    status: 'Pending',
    notes: 'Awaiting review — 2 claims · Auto-detected by FlowRCM ERA inbox',
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    adjustments: [
      { claim_id: null, patient_name: 'Emily Davis',   procedure_code: 'D2740', billed: 945,  paid: 756, adjustment: 189, reason: 'CO-45 — Contractual adjustment' },
      { claim_id: null, patient_name: 'Tyler Johnson', procedure_code: 'D2950', billed: 185,  paid: 148, adjustment: 37,  reason: 'CO-45 — Contractual adjustment' },
    ]
  },
  {
    id: 'era-demo-004',
    payer_name: 'Aetna Dental',
    check_number: 'AE-20260315-2251',
    check_date: '2026-03-15',
    payment_date: '2026-03-15',
    payment_method: 'Check',
    total_amount: 520.00,
    applied_amount: 260.00,
    status: 'Partial',
    notes: 'Partial payment — D8080 ortho — remaining $260 pending secondary insurance',
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    adjustments: [
      { claim_id: null, patient_name: 'Robert Chen', procedure_code: 'D8080', billed: 5800, paid: 520, adjustment: 5280, reason: 'Lifetime ortho max applied — partial benefit' },
    ]
  },
];

// ==================== PRE-AUTH DEMO DATA ====================
export const demoPreAuths = [
  {
    id: 'pa-demo-000',
    patient_name: 'Sarah Mitchell',
    payer_name: 'MetLife Dental',
    procedure_codes: ['D2750'],
    status: 'Approved',
    priority: 'Normal',
    estimated_amount: 890,
    submitted_date: '2026-03-02',
    decision_date: '2026-03-14',
    auth_number: 'ML-AUTH-334812',
    notes: 'Approved — D2750 porcelain crown. Auth valid through 10/02/2026.',
    created_at: new Date(Date.now() - 33 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'pa-demo-001',
    patient_name: 'Sarah Mitchell',
    payer_name: 'Delta Dental PPO',
    procedure_codes: ['D6010', 'D6065'],
    status: 'Approved',
    priority: 'Normal',
    estimated_amount: 3200,
    submitted_date: '2026-03-10',
    decision_date: '2026-03-14',
    auth_number: 'DD-AUTH-882341',
    notes: 'Approved — implant placement and crown. Auth valid through 09/14/2026.',
    created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'pa-demo-002',
    patient_name: 'Tyler Johnson',
    payer_name: 'Cigna Dental PPO',
    procedure_codes: ['D8080'],
    status: 'Approved',
    priority: 'Normal',
    estimated_amount: 5800,
    submitted_date: '2026-03-12',
    decision_date: '2026-03-19',
    auth_number: 'CG-AUTH-441092',
    notes: 'Comprehensive ortho approved. Lifetime max $1,500 applies. Treatment to begin within 60 days.',
    created_at: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'pa-demo-003',
    patient_name: 'Maria Garcia',
    payer_name: 'Delta Dental PPO',
    procedure_codes: ['D2740'],
    status: 'Pending',
    priority: 'High',
    estimated_amount: 1250,
    submitted_date: '2026-03-26',
    decision_date: null,
    auth_number: null,
    notes: 'Submitted — crown tooth #19. Awaiting payer review. Expected 3-5 business days.',
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'pa-demo-004',
    patient_name: 'Robert Chen',
    payer_name: 'Aetna Dental',
    procedure_codes: ['D7210', 'D9930'],
    status: 'Pending',
    priority: 'High',
    estimated_amount: 875,
    submitted_date: '2026-03-27',
    decision_date: null,
    auth_number: null,
    notes: 'Surgical extraction — impacted wisdom tooth #32. Payer requires clinical notes and panoramic radiograph.',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'pa-demo-005',
    patient_name: 'James Torres',
    payer_name: 'MetLife Dental',
    procedure_codes: ['D4341', 'D4342'],
    status: 'Denied',
    priority: 'Normal',
    estimated_amount: 1800,
    submitted_date: '2026-03-08',
    decision_date: '2026-03-15',
    auth_number: null,
    notes: 'Denied — missing periodontal charting documentation. Resubmission with updated records recommended.',
    created_at: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

// Stream of events for the "Run Automation Now" demo — plays out live with delays
export const demoAutomationStream = [
  { delay: 400,  action: 'auto_appeal_generated', message: 'Appeal generated — Sarah Mitchell · MetLife · $890',                      metadata: { patient: 'Sarah Mitchell', payer: 'MetLife', amount: 890 } },
  { delay: 700,  action: 'auto_appeal_generated', message: 'Appeal generated — James Torres · Delta Dental · $1,240',                 metadata: { patient: 'James Torres', payer: 'Delta Dental', amount: 1240 } },
  { delay: 700,  action: 'auto_appeal_generated', message: 'Appeal generated — Maria Garcia · Cigna · $340',                          metadata: { patient: 'Maria Garcia', payer: 'Cigna', amount: 340 } },
  { delay: 500,  action: 'claim_aging_flagged',   message: 'Flagged — Robert Chen · Aetna · 52 days unpaid · $1,820 — HIGH priority', metadata: { patient_name: 'Robert Chen', payer: 'Aetna', amount: 1820, days_old: 52, priority: 'HIGH' } },
  { delay: 500,  action: 'claim_aging_flagged',   message: 'Flagged — Linda Thompson · Blue Cross · 47 days unpaid · $960',           metadata: { patient_name: 'Linda Thompson', payer: 'Blue Cross', amount: 960, days_old: 47, priority: 'MEDIUM' } },
  { delay: 500,  action: 'claim_scrubbed',        message: 'Scrubbed — D2750 Crown · William Brown · Aetna · low denial risk',           metadata: { patient: 'William Brown', payer: 'Aetna' } },
  { delay: 420,  action: 'claim_scrubbed',        message: 'Scrubbed — D3330 Root Canal · Jane Doe · MetLife · coding verified',         metadata: { patient: 'Jane Doe', payer: 'MetLife' } },
  { delay: 420,  action: 'claim_scrubbed',        message: 'Scrubbed — D4341 Perio Scaling · Lisa Thompson · Cigna · approved',          metadata: { patient: 'Lisa Thompson', payer: 'Cigna' } },
  { delay: 400,  action: 'claim_scrubbed',        message: 'Scrubbed — D0272 Bitewing X-Rays · Kevin Park · Delta Dental · approved',    metadata: { patient: 'Kevin Park', payer: 'Delta Dental' } },
  { delay: 400,  action: 'claim_scrubbed',        message: 'Scrubbed — D2391 Composite · Tom Wilson · Cigna · low denial risk',          metadata: { patient: 'Tom Wilson', payer: 'Cigna' } },
  { delay: 380,  action: 'claim_scrubbed',        message: 'Scrubbed — D0120 Periodic Exam · Emily Davis · Guardian · coding verified',  metadata: { patient: 'Emily Davis', payer: 'Guardian' } },
  { delay: 380,  action: 'claim_scrubbed',        message: 'Scrubbed — D1110 Prophylaxis · Michael Lee · Aetna · approved',             metadata: { patient: 'Michael Lee', payer: 'Aetna' } },
  { delay: 400,  action: 'payment_link_sent',     message: 'payment link sent — Jennifer White · $560 balance · email delivered',   metadata: { patient: 'Jennifer White', amount: 560 } },
  { delay: 400,  action: 'payment_link_sent',     message: 'payment link sent — David Anderson · $1,250 balance · email delivered',  metadata: { patient: 'David Anderson', amount: 1250 } },
  { delay: 380,  action: 'payment_link_sent',     message: 'payment link sent — Kevin Park · $320 balance · email delivered',        metadata: { patient: 'Kevin Park', amount: 320 } },
  { delay: 380,  action: 'payment_link_sent',     message: 'payment link sent — Emily Davis · $445 balance · email delivered',       metadata: { patient: 'Emily Davis', amount: 445 } },
  { delay: 500,  action: 'era_posted',            message: 'ERA auto-posted — $2,847 from Delta Dental → 4 claims updated',                         metadata: { payer: 'Delta Dental', amount: 2847 } },
  { delay: 420,  action: 'payment_link_sent',     message: 'payment link auto-sent — Linda Thompson · $336 patient responsibility · email delivered', metadata: { patient: 'Linda Thompson', amount: 336 } },
  { delay: 450,  action: 'era_auto_matched',      message: 'ERA exception auto-matched — Jennifer White · Cigna · $756 → claim updated',            metadata: { patient: 'Jennifer White', payer: 'Cigna', amount: 756 } },
  { delay: 450,  action: 'benefits_verified',     message: 'Benefits verified — Robert Chen · Aetna · $1,500 remaining · deductible met',            metadata: { patient: 'Robert Chen', payer: 'Aetna' } },
  { delay: 400,  action: 'appeal_faxed',          message: 'Appeal auto-faxed — Sarah Mitchell · MetLife · verified contact · docs attached',        metadata: { patient: 'Sarah Mitchell', payer: 'MetLife', amount: 890 } },
  { delay: 400,  action: 'appeal_faxed',          message: 'Appeal auto-faxed — James Torres · Delta Dental · verified contact · docs attached',     metadata: { patient: 'James Torres', payer: 'Delta Dental', amount: 1240 } },
  { delay: 420,  action: 'secondary_claim_created', message: 'Secondary claim auto-created — Emily Davis · MetLife primary paid → Cigna secondary',  metadata: { patient: 'Emily Davis', primary_payer: 'MetLife', secondary_payer: 'Cigna' } },
  { delay: 380,  action: 'appeal_held',           message: 'Appeal held — Tom Wilson · Guardian · fax not verified → billing team notified',          metadata: { patient: 'Tom Wilson', payer: 'Guardian', reason: 'payer_not_verified' } },
  { delay: 400,  action: 'complete',              message: '✓ Done — 3 appeals faxed · 1 held · 2 ERA matched · 1 secondary claim · $2,847 posted · 5 payment links sent',  metadata: null },
];
